/**
 * AttributionContext — web port.
 *
 * Auto-checks attribution on first launch (fingerprint match) and stores
 * `match_id` in localStorage. Claim happens after auth.
 *
 * Differences from RN: replaces MMKV `localStorage` util with browser's
 * `window.localStorage`. URL referral params (`?ref=...`) are picked up
 * automatically on first launch so QR/link arrivals attribute correctly.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  attributionApi,
  type AttributionSource,
  type ClaimAttributionResponse,
} from '../api/attributionApi';
import logger from '../utils/logger';

const STORAGE_KEY_MATCH_ID = 'attribution.match_id';
const STORAGE_KEY_CHECKED = 'attribution.checked';
const STORAGE_KEY_CLAIMED = 'attribution.claimed';

function getString(key: string): string | null {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}
function getBoolean(key: string): boolean {
  return getString(key) === 'true';
}
function setItem(key: string, value: string | boolean): void {
  try {
    if (typeof window !== 'undefined')
      window.localStorage.setItem(key, String(value));
  } catch {}
}

interface AttributionMatchResult {
  matched: boolean;
  match_id?: string;
  referral_source?: { code: string; type: string };
  message?: string;
}

interface AttributionContextType {
  matchId: string | null;
  isChecking: boolean;
  hasChecked: boolean;
  hasClaimed: boolean;
  checkAttribution: (
    clickId?: string,
    referralCode?: string,
    source?: AttributionSource,
    sourceDetail?: string,
  ) => Promise<AttributionMatchResult | null>;
  claimAttribution: (
    authClient: any,
  ) => Promise<ClaimAttributionResponse | null>;
  getMatchId: () => string | null;
}

export const AttributionContext = createContext<AttributionContextType>({
  matchId: null,
  isChecking: false,
  hasChecked: false,
  hasClaimed: false,
  checkAttribution: async () => null,
  claimAttribution: async () => null,
  getMatchId: () => null,
});

function readUrlReferral(): {
  ref?: string;
  clickId?: string;
  sourceDetail?: string;
} {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const ref =
      params.get('ref') ||
      params.get('referral') ||
      params.get('referral_code') ||
      undefined;
    const clickId =
      params.get('click_id') || params.get('clickid') || undefined;
    const sourceDetail = window.location.href;
    return { ref, clickId, sourceDetail };
  } catch {
    return {};
  }
}

export const AttributionProvider = ({ children }: { children: ReactNode }) => {
  const [matchId, setMatchId] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false);

  const checkAttribution = useCallback(
    async (
      clickId?: string,
      referralCode?: string,
      source?: AttributionSource,
      sourceDetail?: string,
    ): Promise<AttributionMatchResult | null> => {
      setIsChecking(true);
      try {
        const result = await attributionApi.checkAttribution(
          clickId,
          referralCode,
          source,
          sourceDetail,
        );

        if (result.matched && result.match_id) {
          setMatchId(result.match_id);
          setItem(STORAGE_KEY_MATCH_ID, result.match_id);
        }

        if (!hasChecked) {
          setHasChecked(true);
          setItem(STORAGE_KEY_CHECKED, true);
        }

        return result;
      } catch (error) {
        logger.error('[Attribution] Check failed', error);
        return null;
      } finally {
        setIsChecking(false);
      }
    },
    [hasChecked],
  );

  useEffect(() => {
    const savedMatchId = getString(STORAGE_KEY_MATCH_ID);
    const savedChecked = getBoolean(STORAGE_KEY_CHECKED);
    const savedClaimed = getBoolean(STORAGE_KEY_CLAIMED);

    if (savedMatchId) setMatchId(savedMatchId);
    if (savedClaimed) setHasClaimed(true);

    if (savedChecked) {
      setHasChecked(true);
      return;
    }

    const { ref, clickId, sourceDetail } = readUrlReferral();
    const source: AttributionSource = ref || clickId ? 'deep_link' : 'app_init';
    const timer = setTimeout(() => {
      checkAttribution(clickId, ref, source, sourceDetail);
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimAttribution = useCallback(
    async (authClient: any): Promise<ClaimAttributionResponse | null> => {
      if (hasClaimed) return null;
      try {
        const result = await attributionApi.claimAttribution(authClient);
        if (result.success) {
          setHasClaimed(true);
          setItem(STORAGE_KEY_CLAIMED, true);
        }
        return result;
      } catch (error) {
        logger.error('[Attribution] Claim failed', error);
        return null;
      }
    },
    [hasClaimed],
  );

  const getMatchId = useCallback(() => matchId, [matchId]);

  const value = useMemo(
    () => ({
      matchId,
      isChecking,
      hasChecked,
      hasClaimed,
      claimAttribution,
      checkAttribution,
      getMatchId,
    }),
    [
      matchId,
      isChecking,
      hasChecked,
      hasClaimed,
      checkAttribution,
      claimAttribution,
      getMatchId,
    ],
  );

  return (
    <AttributionContext.Provider value={value}>
      {children}
    </AttributionContext.Provider>
  );
};

export default AttributionContext;
